import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useFormContext } from '../contexts/FormContext';
import { useNavigate } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';

const Header = () => {
  const { language, setLanguage, t } = useLanguage();
  const { setCurrentStep } = useFormContext();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [languageMenuOpen, setLanguageMenuOpen] = useState(false);

  // Fonction pour gérer les clics sur les liens de navigation
  const handleNavClick = (e, href) => {
    e.preventDefault();
    // Revenir à l'étape 1 pour afficher toutes les sections
    setCurrentStep(1);
    // Attendre un court instant pour que les sections se réaffichent
    setTimeout(() => {
      if (href === '#home') {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        const element = document.querySelector(href);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
    }, 100);
  };

  // Fonction pour gérer le clic sur le logo
  const handleLogoClick = () => {
    setCurrentStep(1);
    navigate(`/${language}`);
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };

  const languageOptions = [
    { code: 'en', label: 'English', flag: '🇬🇧' },
    { code: 'fr', label: 'Français', flag: '🇫🇷' },
    { code: 'es', label: 'Español', flag: '🇪🇸' },
    { code: 'de', label: 'Deutsch', flag: '🇩🇪' },
    { code: 'it', label: 'Italiano', flag: '🇮🇹' },
    { code: 'pt', label: 'Português', flag: '🇵🇹' }
  ];

  const currentLang = languageOptions.find(l => l.code === language) || languageOptions[0];

  return (
    <>
      {/* Top Small Bar */}
      <div className="bg-black/30 backdrop-blur-sm border-b border-white/10">
        <div className="container mx-auto px-6 py-2">
          <p className="text-center text-white text-sm font-light tracking-widest">
            ✨ TRANSFORM YOUR MEMORIES INTO MUSIC ✨
          </p>
        </div>
      </div>

      {/* Main Header - Transparent with backdrop blur */}
      <header className="sticky top-0 z-50 bg-white/40 backdrop-blur-xl border-b border-white/20">
        <div className="container mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            {/* Logo TRAKZ avec icône */}
            <div className="flex items-center gap-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_compose-translate/artifacts/s3iampc6_Logo%20Trakz.png" 
                alt="Trakz Logo" 
                className="h-[50px] w-[50px] lg:h-[60px] lg:w-[60px]"
              />
              <span className="text-4xl lg:text-5xl font-black bg-gradient-to-r from-purple-600 via-pink-500 to-fuchsia-500 bg-clip-text text-transparent tracking-tight">
                TRAKZ
              </span>
            </div>

            {/* Navigation Pill - SÉPARÉE à droite avec langue et search */}
            <div className="hidden lg:flex items-center gap-4">
              {/* Navigation dans sa propre pill beige */}
              <nav className="flex items-center bg-gradient-to-r from-amber-50/90 to-orange-50/90 backdrop-blur-sm px-8 py-4 rounded-full shadow-lg border border-orange-200/50">
                <div className="flex items-center gap-6">
                  <a 
                    href="#home" 
                    onClick={(e) => handleNavClick(e, '#home')}
                    className="text-gray-800 hover:text-purple-600 transition-colors font-medium text-sm tracking-wide"
                  >
                    {t('nav.home')}
                  </a>
                  <span className="text-gray-400">|</span>
                  <a 
                    href="#create" 
                    onClick={(e) => handleNavClick(e, '#create')}
                    className="text-gray-800 hover:text-purple-600 transition-colors font-medium text-sm tracking-wide"
                  >
                    {t('nav.create')}
                  </a>
                  <span className="text-gray-400">|</span>
                  <a 
                    href="#examples" 
                    onClick={(e) => handleNavClick(e, '#examples')}
                    className="text-gray-800 hover:text-purple-600 transition-colors font-medium text-sm tracking-wide"
                  >
                    {t('nav.examples')}
                  </a>
                  <span className="text-gray-400">|</span>
                  <a 
                    href="#reviews" 
                    onClick={(e) => handleNavClick(e, '#reviews')}
                    className="text-gray-800 hover:text-purple-600 transition-colors font-medium text-sm tracking-wide"
                  >
                    {t('nav.reviews')}
                  </a>
                  <span className="text-gray-400">|</span>
                  <a 
                    href="#contact" 
                    onClick={(e) => handleNavClick(e, '#contact')}
                    className="text-gray-800 hover:text-purple-600 transition-colors font-medium text-sm tracking-wide"
                  >
                    Contact
                  </a>
                </div>
              </nav>

              {/* Language Selector - Pill séparée */}
              <div className="relative">
                <button 
                  onClick={() => setLanguageMenuOpen(!languageMenuOpen)}
                  className="flex items-center gap-2 px-4 py-3 bg-white/80 backdrop-blur-sm rounded-full shadow-md hover:shadow-lg transition-all border border-gray-200/50"
                >
                  <span className="text-lg">{currentLang.flag}</span>
                  <span className="text-xs font-bold text-gray-700">{currentLang.code.toUpperCase()}</span>
                  <ChevronDown className="w-3 h-3 text-gray-500" />
                </button>
                
                {languageMenuOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white/95 backdrop-blur-md rounded-2xl shadow-xl border border-gray-200 py-2 z-50">
                    {languageOptions.map((lang) => (
                      <button
                        key={lang.code}
                        onClick={() => {
                          setLanguage(lang.code);
                          setLanguageMenuOpen(false);
                        }}
                        className={`w-full px-4 py-2.5 text-left flex items-center gap-3 hover:bg-gray-50 transition-colors ${
                          language === lang.code ? 'bg-purple-50 text-purple-700' : 'text-gray-700'
                        }`}
                      >
                        <span className="text-lg">{lang.flag}</span>
                        <span className="text-sm font-medium">{lang.label}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden w-12 h-12 rounded-full bg-white/80 backdrop-blur-sm shadow-md flex items-center justify-center"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="lg:hidden mt-6 bg-gradient-to-r from-amber-50/95 to-orange-50/95 backdrop-blur-md rounded-3xl p-6 shadow-xl border border-orange-200/50">
              <nav className="flex flex-col space-y-4">
                <a 
                  href="#home" 
                  onClick={(e) => { handleNavClick(e, '#home'); setMobileMenuOpen(false); }}
                  className="text-gray-800 hover:text-purple-600 transition-colors font-medium py-2"
                >
                  {t('nav.home')}
                </a>
                <div className="h-px bg-gray-300"></div>
                <a 
                  href="#create" 
                  onClick={(e) => { handleNavClick(e, '#create'); setMobileMenuOpen(false); }}
                  className="text-gray-800 hover:text-purple-600 transition-colors font-medium py-2"
                >
                  {t('nav.create')}
                </a>
                <div className="h-px bg-gray-300"></div>
                <a 
                  href="#examples" 
                  onClick={(e) => { handleNavClick(e, '#examples'); setMobileMenuOpen(false); }}
                  className="text-gray-800 hover:text-purple-600 transition-colors font-medium py-2"
                >
                  {t('nav.examples')}
                </a>
                <div className="h-px bg-gray-300"></div>
                <a 
                  href="#reviews" 
                  onClick={(e) => { handleNavClick(e, '#reviews'); setMobileMenuOpen(false); }}
                  className="text-gray-800 hover:text-purple-600 transition-colors font-medium py-2"
                >
                  {t('nav.reviews')}
                </a>
                <div className="h-px bg-gray-300"></div>
                <a 
                  href="#contact" 
                  onClick={(e) => { handleNavClick(e, '#contact'); setMobileMenuOpen(false); }}
                  className="text-gray-800 hover:text-purple-600 transition-colors font-medium py-2"
                >
                  Contact
                </a>
                
                {/* Mobile Language Selector */}
                <div className="pt-4 mt-4 border-t border-gray-300">
                  <p className="text-xs text-gray-500 mb-3 font-medium">SELECT LANGUAGE</p>
                  <div className="grid grid-cols-2 gap-2">
                    {languageOptions.map((lang) => (
                      <button
                        key={lang.code}
                        onClick={() => {
                          setLanguage(lang.code);
                          setMobileMenuOpen(false);
                        }}
                        className={`px-3 py-2 rounded-xl text-sm font-medium flex items-center gap-2 transition-colors ${
                          language === lang.code 
                            ? 'bg-purple-600 text-white shadow-md' 
                            : 'bg-white/80 text-gray-700 hover:bg-white'
                        }`}
                      >
                        <span>{lang.flag}</span>
                        <span>{lang.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </nav>
            </div>
          )}
        </div>
      </header>
    </>
  );
};

export default Header;
